import { useMemo, useState, type FormEvent } from "react";
import { AppearancePanel } from "@athavita/ui-theme";
import {
    useAddSafeSender, useCalendarSettings, usePutCalendarSettings, useRemoveSafeSender, useSafeSenders
} from "@/api/hooks";
import type { CalendarSettings } from "@/api/mailApi";
import { browserTimeZone, offsetLabel, parseClock, timeZoneOptions } from "@/lib/timezone";
import { loadReminderMinutes, REMINDER_CHOICES, saveReminderMinutes } from "@/features/calendar/EventReminders";
import { useConversationView, useShortcutsEnabled } from "@/lib/mailViewPrefs";
import {
    resetPreferences,
    updatePreferences,
    usePreferences,
    usePreferencesSyncStatus,
    type Density,
    type ReadingPanePref,
    type SyncStatus
} from "@/lib/preferences";

const SYNC_TEXT: Record<SyncStatus, string> = {
    synced: "Saved to your account — these follow you to every device.",
    saving: "Saving to your account…",
    offline: "Saved on this device. We'll save to your account when the connection is back.",
    local: "Saved on this device."
};

function Radio<T extends string>({
    name,
    value,
    current,
    label,
    description,
    onChange,
}: {
    name: string;
    value: T;
    current: T;
    label: string;
    description?: string;
    onChange: (v: T) => void;
}) {
    const id = `${name}-${value}`;
    return (
        <label
            htmlFor={id}
            className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${current === value ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                }`}
        >
            <input
                id={id}
                type="radio"
                name={name}
                value={value}
                checked={current === value}
                onChange={() => onChange(value)}
                className="mt-0.5 accent-primary"
            />
            <div>
                <div className="text-sm font-medium">{label}</div>
                {description && (
                    <div className="text-xs text-muted-foreground">{description}</div>
                )}
            </div>
        </label>
    );
}

function Toggle({
    label,
    description,
    checked,
    onChange,
}: {
    label: string;
    description?: string;
    checked: boolean;
    onChange: (v: boolean) => void;
}) {
    return (
        <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
                <div className="text-sm font-medium">{label}</div>
                {description && <div className="text-xs text-muted-foreground">{description}</div>}
            </div>
            <button
                type="button"
                role="switch"
                aria-checked={checked}
                aria-label={label}
                onClick={() => onChange(!checked)}
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary ${checked ? "bg-primary" : "bg-muted-foreground/30"
                    }`}
            >
                <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? "translate-x-4" : "translate-x-0.5"
                        }`}
                />
            </button>
        </div>
    );
}

/** Calendar: Gmail-style "events from email". Saved on the server — the worker runs there. */
function CalendarSection() {
    const settings = useCalendarSettings();
    const put = usePutCalendarSettings();
    if (!settings.data) return null;
    return (
        <section className="space-y-2" id="calendar">
            <h3 className="text-sm font-semibold text-foreground">Calendar</h3>
            <Toggle
                label="Add events from email to my calendar"
                description="Meeting invitations and bookings found in your mail — flights, trains, hotels, tickets, tables — are added to your calendar automatically and kept up to date. This runs on your mail server; nothing is sent anywhere else."
                checked={settings.data.eventsFromEmail}
                onChange={(v) => { if (!put.isPending) put.mutate({ eventsFromEmail: v }); }}
            />
            <WorkingHoursSetting key={settingsKey(settings.data)} settings={settings.data} />
            <ReminderSetting />
        </section>
    );
}

const WEEKDAYS: Array<[number, string]> = [[1, "Mon"], [2, "Tue"], [3, "Wed"], [4, "Thu"], [5, "Fri"], [6, "Sat"], [7, "Sun"]];

const settingsKey = (s: CalendarSettings) => `${s.workDays.join(",")}|${s.workStart}|${s.workEnd}|${s.timeZone}`;

/**
 * Working hours and time zone (Outlook's calendar settings), saved on the
 * server: the scheduling assistant shades everyone's non-working time and
 * suggests slots inside it, and both calendars show times in this zone.
 */
function WorkingHoursSetting({ settings }: { settings: CalendarSettings }) {
    const put = usePutCalendarSettings();
    const [days, setDays] = useState<number[]>(settings.workDays);
    const [start, setStart] = useState(settings.workStart);
    const [end, setEnd] = useState(settings.workEnd);
    const [tz, setTz] = useState(settings.timeZone);
    const zones = useMemo(() => {
        const all = timeZoneOptions();
        return all.includes(tz) ? all : [tz, ...all];
    }, [tz]);
    const deviceZone = browserTimeZone();

    const startMin = parseClock(start);
    const endMin = parseClock(end);
    const invalid = days.length === 0 || startMin === null || endMin === null || endMin <= startMin;
    const dirty = settingsKey({ ...settings, workDays: days, workStart: start, workEnd: end, timeZone: tz }) !== settingsKey(settings);

    const toggleDay = (d: number) =>
        setDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort((a, b) => a - b)));

    return (
        <div className="space-y-3 rounded-lg border p-3">
            <div>
                <div className="text-sm font-medium">Working hours and time zone</div>
                <div className="text-xs text-muted-foreground">
                    Colleagues scheduling a meeting with you see these hours in the scheduling assistant, and your calendars
                    show times in this zone.
                </div>
            </div>
            <div role="group" aria-label="Working days" className="flex flex-wrap gap-1.5">
                {WEEKDAYS.map(([d, labelText]) => (
                    <button
                        key={d}
                        type="button"
                        aria-pressed={days.includes(d)}
                        onClick={() => toggleDay(d)}
                        className={`h-8 w-11 rounded-md border text-xs font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary ${days.includes(d) ? "border-primary bg-primary text-primary-foreground" : "hover:bg-accent"}`}
                    >
                        {labelText}
                    </button>
                ))}
            </div>
            <div className="flex flex-wrap items-end gap-3">
                <label className="text-xs text-muted-foreground">
                    Start
                    <input type="time" value={start} onChange={(e) => setStart(e.target.value)}
                        className="mt-1 block h-8 rounded-md border bg-background px-2 text-sm text-foreground" />
                </label>
                <label className="text-xs text-muted-foreground">
                    End
                    <input type="time" value={end} onChange={(e) => setEnd(e.target.value)}
                        className="mt-1 block h-8 rounded-md border bg-background px-2 text-sm text-foreground" />
                </label>
                <label className="min-w-[14rem] flex-1 text-xs text-muted-foreground">
                    Time zone
                    <select value={tz} onChange={(e) => setTz(e.target.value)}
                        className="mt-1 block h-8 w-full rounded-md border bg-background px-2 text-sm text-foreground">
                        {zones.map((z) => (
                            <option key={z} value={z}>{z.replace(/_/g, " ")} ({offsetLabel(z)})</option>
                        ))}
                    </select>
                </label>
            </div>
            <div className="flex flex-wrap items-center gap-2">
                {deviceZone !== tz ? (
                    <button type="button" onClick={() => setTz(deviceZone)} className="text-xs text-primary underline-offset-2 hover:underline">
                        Use this device's zone ({deviceZone})
                    </button>
                ) : null}
                <span className="ml-auto text-xs" role="status">
                    {invalid ? <span className="text-destructive">Pick at least one day, and an end after the start.</span>
                        : put.isError ? <span className="text-destructive">{put.error instanceof Error ? put.error.message : "Couldn't save"}</span>
                            : put.isSuccess && !dirty ? <span className="text-muted-foreground">Saved</span> : null}
                </span>
                <button
                    type="button"
                    disabled={!dirty || invalid || put.isPending}
                    onClick={() => put.mutate({ workDays: days, workStart: start, workEnd: end, timeZone: tz })}
                    className="h-8 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground disabled:opacity-50"
                >
                    {put.isPending ? "Saving…" : "Save"}
                </button>
            </div>
        </div>
    );
}

/** Per device, like the notification preferences — reminders fire from this browser. */
function ReminderSetting() {
    const [minutes, setMinutes] = useState(loadReminderMinutes);
    return (
        <label className="flex items-center justify-between gap-4 rounded-md border px-3 py-2">
            <span className="min-w-0">
                <span className="block text-sm font-medium text-foreground">Remind me before events</span>
                <span className="block text-xs text-muted-foreground">
                    A desktop notification and a reminder card, on this device.
                </span>
            </span>
            <select
                value={minutes}
                onChange={(e) => {
                    const v = Number(e.target.value);
                    setMinutes(v);
                    saveReminderMinutes(v);
                }}
                className="h-8 shrink-0 rounded-md border bg-background px-2 text-sm"
            >
                {REMINDER_CHOICES.map((m) => (
                    <option key={m} value={m}>{m === 0 ? "Never" : m === 60 ? "1 hour" : `${m} minutes`}</option>
                ))}
            </select>
        </label>
    );
}

/**
 * Safe senders (Outlook's list). Stored on the server, unlike the rest of this
 * page, so it follows the user to every device.
 */
function SafeSendersSection() {
    const list = useSafeSenders();
    const add = useAddSafeSender();
    const remove = useRemoveSafeSender();
    const [value, setValue] = useState("");

    const submit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const address = value.trim();
        if (!address || add.isPending) return;
        add.mutate(address, { onSuccess: () => setValue("") });
    };

    return (
        <section className="space-y-2">
            <h3 className="text-sm font-semibold text-foreground">Safe senders</h3>
            <p className="text-xs text-muted-foreground">
                Pictures and other remote content load automatically only from these senders and from your own
                organisation. Add an address (name@example.com) or a whole domain (@example.com).
            </p>
            <form onSubmit={submit} className="flex gap-2">
                <input
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder="name@example.com or @example.com"
                    aria-label="Safe sender address or domain"
                    className="h-9 min-w-0 flex-1 rounded-md border bg-background px-3 text-sm"
                />
                <button
                    type="submit"
                    disabled={add.isPending || !value.trim()}
                    className="h-9 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground disabled:opacity-50"
                >
                    Add
                </button>
            </form>
            {add.isError ? (
                <p className="text-xs text-destructive">
                    {add.error instanceof Error ? add.error.message : "Could not add this sender."}
                </p>
            ) : null}
            {list.data && list.data.length > 0 ? (
                <ul className="divide-y rounded-lg border">
                    {list.data.map((s) => (
                        <li key={s.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                            <span className="min-w-0 truncate">{s.address}</span>
                            <button
                                type="button"
                                onClick={() => remove.mutate(s.id)}
                                disabled={remove.isPending}
                                className="shrink-0 rounded px-2 py-0.5 text-xs text-muted-foreground hover:bg-accent hover:text-destructive"
                            >
                                Remove
                            </button>
                        </li>
                    ))}
                </ul>
            ) : list.data ? (
                <p className="text-xs text-muted-foreground">No safe senders yet.</p>
            ) : null}
        </section>
    );
}

/**
 * Mode, accent palette and top-bar style — the suite-wide panel every Athavita app
 * shows (the same as the top bar's Appearance menu), with "Different look in Mail".
 */
function AppearanceSection() {
    return (
        <section className="space-y-3">
            <div>
                <h3 className="text-sm font-semibold text-foreground">Appearance</h3>
                <p className="text-xs text-muted-foreground">
                    One look for Mail, Drive, Docs, Sheets, Slides, PDF and Connect — unless you give Mail its own.
                </p>
            </div>
            <div className="max-w-sm rounded-lg border p-3">
                <AppearancePanel app="mail" />
            </div>
        </section>
    );
}

export function GeneralPage() {
    const prefs = usePreferences();
    const sync = usePreferencesSyncStatus();
    const [conversationView, setConversationView] = useConversationView();
    const [shortcutsEnabled, setShortcutsEnabled] = useShortcutsEnabled();

    const setLayout = (patch: Partial<typeof prefs.layout>) => updatePreferences({ layout: patch });
    const setGeneral = (patch: Partial<typeof prefs.general>) => updatePreferences({ general: patch });

    return (
        <div className="max-w-2xl space-y-7">
            <div>
                <h2 className="text-lg font-semibold">General preferences</h2>
                <p className="mt-0.5 text-sm text-muted-foreground" role="status">
                    Adjust how the mail interface looks and behaves. {SYNC_TEXT[sync]}
                </p>
            </div>

            <AppearanceSection />

            <CalendarSection />

            <SafeSendersSection />

            <section className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground">Reading pane</h3>
                <div className="grid gap-2 sm:grid-cols-3">
                    <Radio<ReadingPanePref>
                        name="readingPane"
                        value="right"
                        current={prefs.layout.readingPane}
                        label="Right"
                        description="Side-by-side layout"
                        onChange={(v) => setLayout({ readingPane: v })}
                    />
                    <Radio<ReadingPanePref>
                        name="readingPane"
                        value="bottom"
                        current={prefs.layout.readingPane}
                        label="Bottom"
                        description="Stacked layout"
                        onChange={(v) => setLayout({ readingPane: v })}
                    />
                    <Radio<ReadingPanePref>
                        name="readingPane"
                        value="off"
                        current={prefs.layout.readingPane}
                        label="Off"
                        description="List only — click to open"
                        onChange={(v) => setLayout({ readingPane: v })}
                    />
                </div>
            </section>

            <section className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground">Message density</h3>
                <div className="grid gap-2 sm:grid-cols-3">
                    <Radio<Density>
                        name="density"
                        value="roomy"
                        current={prefs.appearance.density}
                        label="Roomy"
                        description="Taller rows with attachment chips"
                        onChange={(v) => updatePreferences({ appearance: { density: v } })}
                    />
                    <Radio<Density>
                        name="density"
                        value="cozy"
                        current={prefs.appearance.density}
                        label="Cozy"
                        description="Balanced — the default"
                        onChange={(v) => updatePreferences({ appearance: { density: v } })}
                    />
                    <Radio<Density>
                        name="density"
                        value="compact"
                        current={prefs.appearance.density}
                        label="Compact"
                        description="Shorter rows, more messages visible"
                        onChange={(v) => updatePreferences({ appearance: { density: v } })}
                    />
                </div>
            </section>

            <section className="space-y-2">
                <h3 className="text-sm font-semibold text-foreground">Display options</h3>
                <Toggle
                    label="Group by conversation"
                    description="Show replies together as one conversation (also in the message list header)."
                    checked={conversationView}
                    onChange={setConversationView}
                />
                <Toggle
                    label="Keyboard shortcuts"
                    description="Gmail-style single-key shortcuts (j/k, e, #, r, / …). Press ? in Mail for the list."
                    checked={shortcutsEnabled}
                    onChange={setShortcutsEnabled}
                />
                <Toggle
                    label="Show message preview"
                    description="Display the first line of each message in the list"
                    checked={prefs.general.showPreview}
                    onChange={(v) => setGeneral({ showPreview: v })}
                />
            </section>

            <section className="space-y-2">
                <h3 className="text-sm font-semibold text-foreground">Mark as read</h3>
                <div className="rounded-lg border p-3">
                    <label htmlFor="markReadDelay" className="text-sm font-medium">
                        Delay before marking as read
                    </label>
                    <div className="mt-2 flex items-center gap-3">
                        <input
                            id="markReadDelay"
                            type="range"
                            min={0}
                            max={10}
                            step={1}
                            value={prefs.general.markReadDelay}
                            onChange={(e) => setGeneral({ markReadDelay: Number(e.target.value) })}
                            className="flex-1 accent-primary"
                        />
                        <span className="w-20 shrink-0 text-sm text-muted-foreground">
                            {prefs.general.markReadDelay === 0 ? "Immediately" : `${prefs.general.markReadDelay}s`}
                        </span>
                    </div>
                </div>
            </section>

            <div className="flex justify-end">
                <button
                    type="button"
                    onClick={() => resetPreferences()}
                    className="text-sm text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                >
                    Reset to defaults
                </button>
            </div>
        </div>
    );
}
